'use client';

import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useContext, useEffect, useRef } from 'react';
import type { DeferredRequestKey } from '~/constants/common/deferredRequests';
import { useIsTradeLoaded } from '~/context/common/TradeContext';
import { useIsFavoritesLoaded } from '~/context/common/UserContext';
import { useDeliveryPickup } from '~/context/vdp/DeliveryPickupContext';
import { useIsDiscountsLoaded } from '~/context/vdp/DiscountContext';
import StarRatingOwnerReviewContext from '~/context/vdp/StarRatingOwnerReviewContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import VehicleContext from '~/context/vdp/VehicleContext';
import { navPerformanceNow } from '~/utilities/navigationTiming';

export default function useTrackVdpCallLoad() {
  const { trackEvent } = useContext(AnalyticsContext);
  const isPricingLoaded =
    useContext(UserVehicleContext).vehiclePricingInfo.isLoaded;
  const isWhyBuyLoaded = useContext(VehicleContext).ymmtWhyBuy.isLoaded;
  const isOwnerReviewsLoaded = useContext(StarRatingOwnerReviewContext)
    .ratingData.isLoaded;
  const { isLoaded: isDeliveryPickupLoaded } = useDeliveryPickup();
  const isDiscountsLoaded = useIsDiscountsLoaded();
  const isFavoritesLoaded = useIsFavoritesLoaded();
  const isTradeLoaded = useIsTradeLoaded();

  const fired = useRef<Partial<Record<DeferredRequestKey, boolean>>>({});

  const fire = useCallback(
    (callKey: DeferredRequestKey) => {
      const eventData = { callKey, navigationToNowMs: navPerformanceNow() };
      trackEvent('VDP Call Loaded', eventData);
      datadogRum.addAction('VDP Call Loaded', eventData);
    },
    [trackEvent]
  );

  useEffect(() => {
    if (isDeliveryPickupLoaded && !fired.current.deliveryPickup) {
      fired.current.deliveryPickup = true;
      fire('deliveryPickup');
    }
  }, [fire, isDeliveryPickupLoaded]);

  useEffect(() => {
    if (isDiscountsLoaded && !fired.current.discounts) {
      fired.current.discounts = true;
      fire('discounts');
    }
  }, [fire, isDiscountsLoaded]);

  useEffect(() => {
    if (isFavoritesLoaded && !fired.current.favorites) {
      fired.current.favorites = true;
      fire('favorites');
    }
  }, [fire, isFavoritesLoaded]);

  useEffect(() => {
    if (isOwnerReviewsLoaded && !fired.current.ownerReviews) {
      fired.current.ownerReviews = true;
      fire('ownerReviews');
    }
  }, [fire, isOwnerReviewsLoaded]);

  useEffect(() => {
    if (isPricingLoaded && !fired.current.pricing) {
      fired.current.pricing = true;
      fire('pricing');
    }
  }, [fire, isPricingLoaded]);

  useEffect(() => {
    if (isTradeLoaded && !fired.current.trades) {
      fired.current.trades = true;
      fire('trades');
    }
  }, [fire, isTradeLoaded]);

  useEffect(() => {
    if (isWhyBuyLoaded && !fired.current.whyBuy) {
      fired.current.whyBuy = true;
      fire('whyBuy');
    }
  }, [fire, isWhyBuyLoaded]);
}
